import React, { useState, useEffect } from 'react';
import { DonationService } from '../../services/DonationService';
import { ProjectService } from '../../services/ProjectService';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { formatRelativeTime } from '../../utils/dateFormat';
import { Donation } from '../../shared/models/Donation';
import { Project } from '../../shared/models/Project';
import { TopBar } from '../../components/TopBar/TopBar';
import { Pagination } from '../../components/Pagination/Pagination';
import './MyCertificatesView.css';

interface MyCertificatesViewProps {
  onBack: () => void;
  onViewCertificate?: (donation: Donation, project: Project) => void;
}

interface CertificateItem {
  donation: Donation;
  project: Project;
}

/**
 * 捐赠证书列表页面
 */
export const MyCertificatesView: React.FC<MyCertificatesViewProps> = ({
  onBack,
  onViewCertificate,
}) => {
  const { organization } = useOrganization();
  const [certificates, setCertificates] = useState<CertificateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息', 'MyCertificatesView.loadCertificates');
        setLoading(false);
        return;
      }

      // 获取所有已支付的捐赠记录
      const allDonations: Donation[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params: any = {
          donorOpenId: openId,
          page: currentPage,
          limit: 100,
          status: 'PAID',
        };
        // 根据机构类型决定使用哪个过滤参数
        if (organization?.type === 'PUBLIC') {
          params.organizationId = organization.id;
        } else if (organization?.type === 'EXECUTING') {
          params.executingOrganizationId = organization.id;
        }
        const response = await DonationService.getDonations(params);

        allDonations.push(...response.list.filter(d => d.status === 'PAID'));
        hasMore = currentPage < response.totalPages;
        currentPage++;

        // 防止无限循环
        if (currentPage > 100) {
          break;
        }
      }

      // 获取项目详情
      const projectIds = Array.from(new Set(allDonations.map(d => d.projectId).filter(Boolean)));
      const projectPromises = projectIds.map(id => ProjectService.getProjectById(id));
      const projectList = await Promise.all(projectPromises);
      const projectMap = new Map<string, Project>();
      projectList.forEach(project => {
        if (project) {
          projectMap.set(project.id, project);
        }
      });

      // 构建证书列表（每个已支付的捐赠都有一张证书）
      const certificateItems: CertificateItem[] = [];
      allDonations.forEach(donation => {
        const project = projectMap.get(donation.projectId);
        if (project) {
          certificateItems.push({
            donation,
            project,
          });
        }
      });

      // 按支付时间降序排序
      certificateItems.sort((a, b) => {
        const timeA = new Date(a.donation.paymentTime || a.donation.createTime).getTime();
        const timeB = new Date(b.donation.paymentTime || b.donation.createTime).getTime();
        return timeB - timeA;
      });

      setTotal(certificateItems.length);

      // 分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setCertificates(certificateItems.slice(startIndex, endIndex));
    } catch (error) {
      logError('[MyCertificatesView] Failed to load certificates', error);
      showError('加载证书列表失败', 'MyCertificatesView.loadCertificates');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewCertificate = (item: CertificateItem) => {
    if (onViewCertificate) {
      onViewCertificate(item.donation, item.project);
    }
  };

  if (loading && certificates.length === 0) {
    return (
      <div className="my-certificates-view">
        <TopBar title="捐赠证书" showBack onBack={onBack} />
        <div className="my-certificates-loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="my-certificates-view">
      <TopBar title="捐赠证书" showBack onBack={onBack} />
      
      <div className="my-certificates-content">
        {certificates.length === 0 ? (
          <div className="my-certificates-empty">暂无证书</div>
        ) : (
          <>
            <div className="my-certificates-list">
              {certificates.map((item) => {
                const project = item.project;
                const donation = item.donation;
                const displayAmount = donation.donationType === 'MONTHLY' && donation.totalDonationAmount
                  ? donation.totalDonationAmount
                  : donation.amount;
                const paymentTime = donation.paymentTime || donation.createTime;

                return (
                  <div key={donation.id} className="my-certificates-item">
                    <div className="my-certificates-item-content">
                      <div className="my-certificates-item-title">{project.title}</div>
                      <div className="my-certificates-item-time">
                        {formatRelativeTime(paymentTime)}
                      </div>
                    </div>
                    <div className="my-certificates-item-right">
                      <div className="my-certificates-item-amount">{displayAmount.toFixed(2)}元</div>
                      <button
                        className="my-certificates-item-btn"
                        onClick={() => handleViewCertificate(item)}
                      >
                        查看证书
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {loading && (
              <div className="my-certificates-loading-more">加载中...</div>
            )}

            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

